import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { Container } from '@material-ui/core';
import { ThemeProvider, makeStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import TagManager from 'react-gtm-module';
import mixpanel from 'mixpanel-browser';
import * as Sentry from "@sentry/react";

import { AppStateComponent, AppContext } from '../components/AppState';
import { TextureAuthProvider } from '../utils/textureAuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import theme from '../utils/theme';
import './global.css';


const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: '100vh',
  },
  footer: {
    marginTop: 'auto',
  }
}));


function MyApp({ Component, pageProps }) {
  const classes = useStyles();
  const router = useRouter();

  const [isMixPanelReady, setIsMixPanelReady] = React.useState(false);

  const onRedirectCallback = (appState) => {
    router.replace(appState?.returnTo || '/home');
  }

  useEffect(() => {
    async function onLoad() {
      mixpanel.init(process.env.NEXT_PUBLIC_MIXPANEL_TOKEN, {
        debug: (process.env.NEXT_PUBLIC_STAGE_RESOURCE !== 'prod'),
        track_pageview: false,
        persistence: 'localStorage'
      });
      setIsMixPanelReady(true);

      // Remove the server-side injected CSS
      const jssStyles = document.querySelector('#jss-server-side');
      if (jssStyles) {
        jssStyles.parentElement.removeChild(jssStyles);
      }

      TagManager.initialize({ gtmId: process.env.NEXT_PUBLIC_GTM_ID });
    };
    onLoad();

    const handleRouteChange = (url) => {
      // Hubspot Tracking Code API.
      let _hsq = window._hsq = window._hsq || [];
      _hsq.push(['setPath', url]);
      _hsq.push(['trackPageView']);

      Sentry.addBreadcrumb({
        category: 'navigation',
        message: url,
        level: 'info',
      });
    }

    router.events.on('routeChangeStart', handleRouteChange);

    // If the component is unmounted, unsubscribe
    // from the event with the `off` method:
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (<>
    <Head>
      <title>Bandicoot - App</title>
    </Head>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Auth0Provider
        domain={process.env.NEXT_PUBLIC_AUTH0_DOMAIN}
        clientId={process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID}
        onRedirectCallback={onRedirectCallback}
        cacheLocation='localstorage'
        useRefreshTokens={true}
        useRefreshTokensFallback={true}
        authorizationParams={{
          audience: process.env.NEXT_PUBLIC_AUTH0_AUDIENCE,
          redirect_uri: process.env.NEXT_PUBLIC_AUTH0_REDIRECT_URI,
          scope: 'profile email write:materials',
        }}
      >
        <TextureAuthProvider>
          <AppStateComponent isMixPanelReady={isMixPanelReady}>
          <AppContext.Consumer>
            {({ isCollab }) => (
              <div className={classes.container}>
                <Navbar hidden={isCollab}/>
                <Container maxWidth={false}>
                  <Component {...pageProps} />
                </Container>
                {!isCollab &&
                  <div className={classes.footer}>
                    <Footer />
                  </div>
                }
              </div>
            )}
          </AppContext.Consumer>
        </AppStateComponent>
        </TextureAuthProvider>
      </Auth0Provider>
    </ThemeProvider>
  </>);
}

export default MyApp;
